import type { ReactNode } from 'react';
import { cn } from '../cn.ts';

/**
 * `size` và `tone` đều là vai trong thiết kế, không phải thang áo.
 *
 * `tone` on-dark-* trỏ vào biến trong `:root` của `theme.css` (26 mức trắng-alpha của thiết kế).
 * Chúng không phải màu `@theme` nên không có utility sẵn — cách đúng là dùng dạng arbitrary value
 * trỏ vào biến, thay vì viết giá trị trắng-alpha thẳng vào component.
 *
 * Cú pháp class cố tình KHÔNG viết ra trong comment: Tailwind v4 quét file nguồn như văn bản
 * thuần, nên một cú pháp có dấu ba chấm ở đây sinh ra rule CSS rác trỏ vào biến không tồn tại.
 */
export interface TextProps {
  readonly children: ReactNode;
  readonly size?: 'lead-lg' | 'lead' | 'lead-compact' | 'body' | 'body-sm' | 'caption';
  /**
   * Bốn bậc trên nền tối đặt tên theo MỨC, không theo section — `on-dark-78` là mức, `on-dark-hero`
   * thì không: `#ai` và `#security` dùng hai mức khác nhau nên tên theo section sẽ nói dối ngay ở
   * hộ dùng thứ hai. `on-dark` giữ lại làm bí danh của 78 vì hero và lô B đang dùng tên đó.
   *
   * Thiết kế dùng đúng bốn mức cho lead trên nền tối: 80 (`#cta`) · 78 (hero) · 74 (`#ai`) ·
   * 66 (`#security`). Bản đầu chỉ phơi 78 và 60, nên lô C phải ráp tay khối đầu `#ai` để khỏi
   * đụng package này, và lô D — lô cuối, không còn ai vá sau — sẽ đâm y hệt ở mức 66.
   */
  readonly tone?:
    | 'strong'
    | 'default'
    | 'muted'
    | 'on-dark-80'
    | 'on-dark'
    | 'on-dark-74'
    | 'on-dark-66'
    | 'on-dark-soft';
  readonly className?: string;
}

const SIZE = {
  'lead-lg': 'text-lead-lg',
  lead: 'text-lead',
  'lead-compact': 'text-lead-compact',
  body: 'text-body',
  'body-sm': 'text-body-sm',
  caption: 'text-caption',
} as const;

const TONE = {
  strong: 'text-ink-900',
  default: 'text-ink-600',
  muted: 'text-ink-500',
  'on-dark-80': 'text-[var(--on-dark-text-80)]',
  'on-dark': 'text-[var(--on-dark-text-78)]',
  'on-dark-74': 'text-[var(--on-dark-text-74)]',
  'on-dark-66': 'text-[var(--on-dark-text-66)]',
  'on-dark-soft': 'text-[var(--on-dark-text-60)]',
} as const;

export function Text({ children, size = 'body', tone = 'default', className }: TextProps) {
  return <p className={cn('text-pretty', SIZE[size], TONE[tone], className)}>{children}</p>;
}
